import type { ReactNode } from 'react';
import { useEffectiveLayout } from '../hooks/useLayout';
import { panelVars } from '../lib/panelStyle';
import { useSettings } from '../store/settings';
import { CustomLayout } from './CustomLayout';
import { WidgetBoundary } from './WidgetBoundary';
import { Calendar } from './widgets/Calendar';
import { Clock } from './widgets/Clock';
import { Lists } from './widgets/Lists';
import { NewsCard } from './widgets/NewsCard';
import { NewsTicker } from './widgets/NewsTicker';
import { PhotoInfo } from './widgets/PhotoInfo';
import { Weather } from './widgets/Weather';

function W({ name, children }: { name: string; children: ReactNode }) {
  return <WidgetBoundary name={name}>{children}</WidgetBoundary>;
}

/** The widget overlay, with the panel look from Settings → Appearance → Widget style. */
export function Layout() {
  const widgetStyle = useSettings((s) => s.widgetStyle);
  const theme = useSettings((s) => s.theme);
  // display: contents adds no box; the custom properties still reach every widget below.
  return (
    <div className="contents" style={panelVars(widgetStyle, theme)}>
      <Arrangement />
    </div>
  );
}

/** Arranges the widget overlay above the media stage according to the chosen layout. */
function Arrangement() {
  const layout = useEffectiveLayout();
  const weather = useSettings((s) => s.weather.enabled);
  const calendar = useSettings((s) => s.calendar.enabled);
  const lists = useSettings((s) => s.lists.enabled);
  const news = useSettings((s) => s.news.enabled);
  const clock = useSettings((s) => s.clock.enabled);
  const photoInfo = useSettings((s) => s.photoInfo.enabled);
  const photosOnly = useSettings((s) => s.photosOnly);
  const newsDisplay = useSettings((s) => s.news.display);
  // Week/Month grids need the full width where the layout allows it.
  const wideCalendar = useSettings((s) => s.calendar.view === 'week' || s.calendar.view === 'month');

  // Photos only: just the slideshow.
  if (photosOnly) return null;

  const card = news && newsDisplay !== 'ticker' && (
    <W name="News">
      <NewsCard />
    </W>
  );
  const cardCompact = news && newsDisplay !== 'ticker' && (
    <W name="News">
      <NewsCard compact />
    </W>
  );
  // Hides itself for photos with no place or date.
  const info = (className?: string) =>
    photoInfo && (
      <W name="Location">
        <PhotoInfo className={className} />
      </W>
    );
  const ticker = news && newsDisplay !== 'card' && (
    <W name="News">
      <NewsTicker />
    </W>
  );

  if (layout === 'custom') return <CustomLayout ticker={ticker} />;

  if (layout === 'portrait-stack') {
    // Photo fills the top (MediaStage is confined there by App); widgets in a solid panel below.
    return (
      <div className="absolute inset-0 flex flex-col">
        <div className="pointer-events-none relative h-[42%] shrink-0">
          {clock && (
            // The whole photo area, so the clock can be placed anywhere in it.
            <div className="absolute inset-0 p-6 md:p-8">
              <Clock size="lg" />
            </div>
          )}
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-[var(--surface-strong)] p-6">
          {weather && (
            <W name="Weather">
              <Weather />
            </W>
          )}
          {info()}
          <div className="grid grid-cols-2 gap-4">
            {calendar && (
              <div className={lists && !wideCalendar ? '' : 'col-span-2'}>
                <W name="Calendar">
                  <Calendar maxEvents={8} />
                </W>
              </div>
            )}
            {lists && (
              <div className={calendar && !wideCalendar ? '' : 'col-span-2'}>
                <W name="Lists">
                  <Lists />
                </W>
              </div>
            )}
          </div>
          {card}
        </div>
        {ticker}
      </div>
    );
  }

  if (layout === 'portrait-glass') {
    return (
      <div className="absolute inset-0 flex flex-col">
        <div className="pointer-events-none p-8 pt-16">{clock && <Clock size="xl" />}</div>
        <div className="flex-1" />
        <div className="flex max-h-[62%] flex-col gap-4 overflow-y-auto p-6">
          {info()}
          {calendar && (
            <W name="Calendar">
              <Calendar maxEvents={5} />
            </W>
          )}
          <div className="grid grid-cols-2 gap-4">
            {weather && (
              <div className={card ? '' : 'col-span-2'}>
                <W name="Weather">
                  <Weather compact />
                </W>
              </div>
            )}
            {card && <div className={weather ? '' : 'col-span-2'}>{cardCompact}</div>}
          </div>
          {lists && (
            <W name="Lists">
              <Lists />
            </W>
          )}
        </div>
        {ticker}
      </div>
    );
  }

  if (layout === 'cinematic') {
    return (
      <div className="pointer-events-none absolute inset-0 flex flex-col">
        <div className="flex-1" />
        <div className="pointer-events-auto flex items-end justify-between gap-6 p-8 md:p-12">
          {clock ? <Clock size="xl" /> : <div />}
          {(weather || card || photoInfo) && (
            <div className="hidden w-96 flex-col gap-4 md:flex">
              {info()}
              {card}
              {weather && (
                <W name="Weather">
                  <Weather compact />
                </W>
              )}
            </div>
          )}
        </div>
        <div className="pointer-events-auto">{ticker}</div>
      </div>
    );
  }

  if (layout === 'command') {
    return (
      <div className="absolute inset-0 flex flex-col">
        <div className="flex min-h-0 flex-1">
          <div className="flex min-w-0 flex-1 p-8 md:p-12">{clock && <Clock size="lg" />}</div>
          <aside className="flex w-full max-w-sm flex-col gap-4 overflow-y-auto p-6">
            {weather && (
              <W name="Weather">
                <Weather compact />
              </W>
            )}
            {info()}
            {calendar && (
              <W name="Calendar">
                <Calendar maxEvents={6} />
              </W>
            )}
            {lists && (
              <W name="Lists">
                <Lists />
              </W>
            )}
            {cardCompact}
          </aside>
        </div>
        {ticker}
      </div>
    );
  }

  if (layout === 'bento') {
    return (
      <div className="absolute inset-0 flex flex-col">
        <div className="grid min-h-0 flex-1 auto-rows-min grid-cols-1 content-end gap-4 overflow-y-auto p-6 md:grid-cols-4 md:p-10">
          {clock && (
            <div className="glass flex items-end p-6 md:col-span-2">
              <Clock size="lg" boxable={false} />
            </div>
          )}
          {weather && (
            <div className="md:col-span-2">
              <W name="Weather">
                <Weather />
              </W>
            </div>
          )}
          {calendar && (
            <div className={wideCalendar ? 'md:col-span-4' : 'md:col-span-2'}>
              <W name="Calendar">
                <Calendar maxEvents={5} />
              </W>
            </div>
          )}
          {lists && (
            <div className="md:col-span-2">
              <W name="Lists">
                <Lists />
              </W>
            </div>
          )}
          {card && <div className="md:col-span-2">{card}</div>}
          {info('md:col-span-2')}
        </div>
        {ticker}
      </div>
    );
  }

  // split
  return (
    <div className="absolute inset-0 flex flex-col">
      <div className="flex min-h-0 flex-1">
        <aside className="flex w-full max-w-md flex-col gap-5 overflow-y-auto bg-[var(--surface-strong)] p-8">
          {clock && <Clock size="md" />}
          {info()}
          {weather && (
            <W name="Weather">
              <Weather compact />
            </W>
          )}
          {calendar && (
            <W name="Calendar">
              <Calendar maxEvents={8} />
            </W>
          )}
          {lists && (
            <W name="Lists">
              <Lists />
            </W>
          )}
          {cardCompact}
        </aside>
        <div className="flex-1" />
      </div>
      {ticker}
    </div>
  );
}
